/**
 * `LedgerShadow` — slice **L3** (LR5 "declare", running **dark**) of the
 * row-identity ledger design
 * (§3.4, §4 LR5, §7). It mirrors the old formula/derived dependency graph
 * (`sheet.graph`, keyed by `RowLocalId`) onto the ledger (keyed by `Address`
 * via `AddressOf`), generation-tagged, so the ledger accumulates the SAME edge
 * set the old graph carries — one auditable step before any consumer trusts it.
 *
 * ## Dark by construction (D4)
 *
 * Nothing here reads the ledger to make a decision. `declareFromGraph` /
 * `syncAllFromGraph` only *write* declarations; `checkAndRecord` is the **LI2
 * referee** — it only *observes* (compares the freshly-computed D4 signature
 * against the ledger's freshness verdict) and counts. Old-graph behavior is
 * byte-identical: the seams add a call *beside* the existing
 * `registerDependencyEdges` / `rebindFormulaDataSource`, never reorder or
 * replace one.
 *
 * ## TDZ constraint (copy of `ledger-binding.ts`'s rule)
 *
 * This module *value*-imports `RowAddress` (and, through it,
 * `../rows/variants/computed-data-source.ts` → `../rows/variants/source-row.ts`). It is
 * therefore only safe to value-import from modules that are NOT on
 * `../rows/variants/source-row.ts`'s import path — otherwise it closes the eval cycle
 * `source-row → X → row-address → computed-data-source → source-row` and
 * TDZ-crashes on the `extends SourceRow` evaluation (learning
 * `2026-07-09-sheets-module-tdz-cycle-ledger-keys.toml`; js.rows is the runner
 * that catches it). Only `raw-sheet.ts` value-imports this module (to construct
 * the shadow at hydrate); the store surface (`stores/source-cell-data.ts`,
 * which nothing on the variant chain pulls) imports it **type-only**. Variant
 * classes reach the live instance through
 * `this.sheet.stores.sourceCellData.ledgerShadow` and MUST NOT import this file.
 */

import type { Option } from "../core/option.ts";
import { Json } from "../core/json.ts";
import type { NaiveDate } from "@tento-chrono";
import type {
  DependencyAspect,
  DependencyEdgeReads,
  DependencyGraphReads,
} from "@tento-core/dependency-graph";
import type { Ledger } from "@tento-core/ledger";
import { JsonReader, type JsonValue } from "@tento-core/json-reader";
import { Tracing } from "@tento-core/tracing";

import { RowAddress } from "../rows/row-address.ts";
import { LedgerKeys, type SheetLedger } from "../stores/ledger-keys.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import type { ModelRow } from "../rows/model-row.ts";

/**
 * Scope of a shadow edge. L3 declares every mirrored edge with `scope: null`
 * (the old formula/derived graph carries no range scope — its edges are
 * unconditional row→row links); the type is bound to the same `NaiveDate.Range`
 * the `SheetLedger` uses so L4 can start declaring real scopes without a type
 * change.
 */
type Scope = NaiveDate.Range;

/**
 * The per-sheet shadow. Reached by the recompute seams via
 * `sheet.stores.sourceCellData.ledgerShadow`; constructed at hydrate only when
 * a ledger exists (standalone / legacy hydrate has no ledger → the field is
 * `null` and every seam is a `?.` no-op, D5).
 */
export interface LedgerShadow {
  /**
   * Late-bind the `RowLocalId → ModelRow` resolver (used to translate an old
   * edge's `targetRowId` producer to its address). Bound at hydrate alongside
   * the L2 `LedgerBinding.attachRowLookup`, before any recompute runs.
   */
  attachRowLookup(lookup: (id: RowLocalId) => Option<ModelRow>): void;

  /**
   * Mirror `consumer`'s current old-graph edges onto the ledger, replacing its
   * prior declaration at the next generation (LR5). Placement-only consumer →
   * skip (nothing to declare); placement-only / unresolved producer → skip that
   * edge. Total — never throws on weird rows.
   */
  declareFromGraph(
    graph: DependencyGraphReads<RowLocalId>,
    consumer: ModelRow,
  ): void;

  /**
   * Hydrate-site bulk declare: every row in `rows` that has edges in `graph`
   * (the formula consumers `buildDependencyGraph` just wired) is declared.
   * Edge-free rows are skipped so the ledger doesn't gain a node per row.
   */
  syncAllFromGraph(
    graph: DependencyGraphReads<RowLocalId>,
    rows: Iterable<ModelRow>,
  ): void;

  /**
   * The **LI2 referee** (design §8): given a consumer's freshly-computed D4
   * `recomputeSignature`, decide whether the ledger's freshness verdict agrees.
   * One-way, range-conditioned: when the ledger reports *clean* (no declared
   * producer moved since the last record) **and** the recompute is over the
   * same range as the last one, the stripped signature MUST be unchanged — a
   * mismatch is a routing divergence (surfaced loud on `ledger/li2-divergence`, never a
   * behavior change). Always records the new snapshot for the next call.
   */
  checkAndRecord(consumer: ModelRow, signature: string): void;

  /** Pull-based aggregate counters for tests. */
  snapshot(): LedgerShadow.Counters;
}

export namespace LedgerShadow {
  /** Aggregate-only observability (the L2 bumper pattern). */
  export interface Counters {
    /** Consumers declared (data-kind, edge set mirrored). */
    readonly declares: number;
    /** Declarations skipped because the consumer is placement-only. */
    readonly consumersSkipped: number;
    /** Individual producer edges skipped (placement-only or unresolved). */
    readonly producersSkipped: number;
    /** Clean same-range LI2 comparisons actually performed. */
    readonly li2Checks: number;
    /** Of those, comparisons where the stripped signature diverged. */
    readonly li2Divergences: number;
  }

  /** Build a shadow over the sheets-side ledger. */
  export function make(ledger: SheetLedger): LedgerShadow {
    return new Impl(ledger);
  }

  /**
   * The D4 signature minus its **range** (`r:`, top-level) and **resolved
   * row-id** (`row:`, embedded per source in the `d` fragment) contributions —
   * both move without a data change (design §8 LI2 / OQ8), so the LI2 referee
   * must not treat their movement as divergence. Pure + total (the input is
   * always `recomputeSignature`'s `JSON.stringify` output, so parsing is safe);
   * unit-tested directly. See `computed-data-source.ts:203` (`recomputeSignature`
   * — `{ r, a, d }`) and `derived-data-source.ts:176` (`dependencySignature` —
   * `d` is a nested JSON string of `{ row, cells }` per source) for the shape.
   */
  export function stripSignature(sig: string): string {
    const parsed = parseSignature(sig);
    const strippedD = parsed.d != null ? stripDependency(parsed.d) : parsed.d;
    // Drop `r` by omission; keep `a` (config is a real input) and the
    // row-stripped `d`.
    return JSON.stringify({ a: parsed.a ?? null, d: strippedD ?? null });
  }

  /** The recompute signature's range marker (`r:`), for same-range gating. */
  export function rangeMarker(sig: string): string {
    const parsed = parseSignature(sig);
    return JSON.stringify(parsed.r ?? null);
  }

  /** The LI2 referee's per-consumer state (a producer-version snapshot + the
   *  last stripped signature + the range it was taken at). */
  interface Snapshot {
    readonly producerVersions: Map<Ledger.Key, Map<DependencyAspect, number>>;
    readonly stripped: string;
    readonly rangeMarker: string;
  }

  /** `recomputeSignature`'s top-level shape (`a` is an arbitrary config bag). */
  interface SignatureShape {
    readonly r: Option<JsonValue>;
    readonly a: Option<JsonValue>;
    readonly d: Option<string>;
  }

  /** Parse a `recomputeSignature` string; names the boundary when it is not
   *  the `{ r, a, d }` object (`d` must be a string when present). */
  function parseSignature(sig: string): SignatureShape {
    const obj = JsonReader.parseObject(sig, "LedgerShadow.signature");
    const d = obj.d ?? null;
    if (d !== null && typeof d !== "string") {
      throw new TypeError("LedgerShadow.signature.d must be a string");
    }
    return { r: obj.r ?? null, a: obj.a ?? null, d };
  }

  class Impl implements LedgerShadow {
    #lookup: Option<(id: RowLocalId) => Option<ModelRow>> = null;

    /** Per-consumer monotonic recompute generation (LR5 / LI7). */
    readonly #generations = new Map<Ledger.Key, number>();

    /** LI2 referee state, keyed by consumer address. */
    readonly #snapshots = new Map<Ledger.Key, Snapshot>();

    // Aggregate counters (no per-edge / per-declare logs).
    #declares = 0;
    #consumersSkipped = 0;
    #producersSkipped = 0;
    #li2Checks = 0;
    #li2Divergences = 0;

    readonly #ledger: SheetLedger;
    constructor(ledger: SheetLedger) {
      this.#ledger = ledger;
    }

    attachRowLookup(lookup: (id: RowLocalId) => Option<ModelRow>): void {
      this.#lookup = lookup;
    }

    declareFromGraph(
      graph: DependencyGraphReads<RowLocalId>,
      consumer: ModelRow,
    ): void {
      const res = RowAddress.of(consumer);
      if (res.kind !== "data") {
        this.#consumersSkipped += 1;
        return;
      }
      const consumerKey = LedgerKeys.data(res.address);

      const edges: Ledger.EdgeInput<Scope>[] = [];
      for (const edge of graph.edgesOf(consumer.id)) {
        const producer = this.#producerKey(edge);
        if (producer == null) {
          this.#producersSkipped += 1;
          continue;
        }
        // Facets are copied from the old edge's aspects; `declareEdges` clones
        // the iterable into an owned Set, so passing the read view is safe.
        edges.push({ producer, facets: edge.aspects, scope: null });
      }

      const generation = (this.#generations.get(consumerKey) ?? 0) + 1;
      this.#generations.set(consumerKey, generation);
      this.#ledger.declareEdges(consumerKey, generation, edges);
      this.#declares += 1;
    }

    syncAllFromGraph(
      graph: DependencyGraphReads<RowLocalId>,
      rows: Iterable<ModelRow>,
    ): void {
      for (const row of rows) {
        if (graph.edgesOf(row.id).length === 0) continue;
        this.declareFromGraph(graph, row);
      }
    }

    checkAndRecord(consumer: ModelRow, signature: string): void {
      const res = RowAddress.of(consumer);
      if (res.kind !== "data") return; // placement-only: no data node to referee
      const consumerKey = LedgerKeys.data(res.address);

      const stripped = LedgerShadow.stripSignature(signature);
      const range = LedgerShadow.rangeMarker(signature);

      const prev = this.#snapshots.get(consumerKey);
      if (prev != null) {
        const clean = this.#snapshotStillHolds(prev.producerVersions);
        const sameRange = prev.rangeMarker === range;
        // One-way + range-conditioned (LI2 / OQ8): only a clean, same-range
        // recompute may be compared. Not-clean (a producer bumped) or a range
        // move → skip the check, just record. First call → no `prev` → record.
        if (clean && sameRange) {
          this.#li2Checks += 1;
          if (prev.stripped !== stripped) {
            this.#li2Divergences += 1;
            Tracing.log("ledger/li2-divergence", {
              consumer: consumerKey,
              generation: this.#generations.get(consumerKey) ?? 0,
              // Lengths only — never the signatures themselves (bounded payload).
              strippedLen: stripped.length,
              lastLen: prev.stripped.length,
            });
          }
        }
      }

      this.#snapshots.set(consumerKey, {
        producerVersions: this.#snapshotProducerVersions(consumerKey),
        stripped,
        rangeMarker: range,
      });
    }

    snapshot(): LedgerShadow.Counters {
      return {
        declares: this.#declares,
        consumersSkipped: this.#consumersSkipped,
        producersSkipped: this.#producersSkipped,
        li2Checks: this.#li2Checks,
        li2Divergences: this.#li2Divergences,
      };
    }

    /** Translate an old edge's producer (`targetRowId`) to its ledger key, or
     *  `null` when the producer is unresolved / placement-only. */
    #producerKey(edge: DependencyEdgeReads<RowLocalId>): Option<Ledger.Key> {
      const row = this.#lookup?.(edge.targetRowId) ?? null;
      if (row == null) return null;
      const res = RowAddress.of(row);
      if (res.kind !== "data") return null;
      return LedgerKeys.data(res.address);
    }

    /** Capture `(producer, facet) → version` for the consumer's CURRENT
     *  declaration — the baseline the next `checkAndRecord` compares against. */
    #snapshotProducerVersions(
      consumerKey: Ledger.Key,
    ): Map<Ledger.Key, Map<DependencyAspect, number>> {
      const out = new Map<Ledger.Key, Map<DependencyAspect, number>>();
      for (const producer of this.#ledger.producersOf(consumerKey)) {
        const facets = this.#ledger.edgeFacets(consumerKey, producer);
        if (facets == null) continue;
        const versions = new Map<DependencyAspect, number>();
        for (const facet of facets) {
          versions.set(facet, this.#ledger.version(producer, facet));
        }
        out.set(producer, versions);
      }
      return out;
    }

    /** True when every snapshotted `(producer, facet)` version is unchanged —
     *  i.e. nothing the consumer declared a dependency on has bumped since. An
     *  empty snapshot (no declared producers) holds vacuously. */
    #snapshotStillHolds(
      snapshot: Map<Ledger.Key, Map<DependencyAspect, number>>,
    ): boolean {
      for (const [producer, facets] of snapshot) {
        for (const [facet, version] of facets) {
          if (this.#ledger.version(producer, facet) !== version) return false;
        }
      }
      return true;
    }
  }

  /** Strip every `row` key from the `d` (dependency) fragment — a nested JSON
   *  string of `{ row, cells }` per source. `""` (base `dependencySignature`)
   *  and any non-array shape pass through unchanged. */
  function stripDependency(d: string): string {
    if (d === "") return d;
    const parsed: JsonValue = Json.wire(JSON.parse(d)) ?? null;
    if (!Array.isArray(parsed)) return d;
    const entries: readonly JsonValue[] = parsed;
    const cleaned = entries.map((entry) =>
      isJsonObject(entry) ? omitKey(entry, "row") : entry
    );
    return JSON.stringify(cleaned);
  }

  function isJsonObject(value: JsonValue): value is JsonReader.Object {
    return value !== null && typeof value === "object" && !Array.isArray(value);
  }

  /** A shallow copy of `obj` without `key`. */
  function omitKey(
    obj: JsonReader.Object,
    key: string,
  ): JsonReader.Object {
    const out: Record<string, JsonValue> = {};
    for (const [k, v] of Object.entries(obj)) if (k !== key) out[k] = v;
    return out;
  }
}
